/* eslint-disable no-unused-vars */
import { useContext,useEffect, useMemo, useRef, useState } from "react";
import { useLocation,useNavigate } from "react-router-dom";

import ProfileDataContext from '@/context/ProfileDataContext';
import ChannelsService from '@/services/channels.service';
import RecommendedArticlesList from '@/shared-components/forms/add-board-content/add-article/published-article/RecommendedArticlesList';
import BoardTextBody from '@/shared-components/forms/add-board-content/board-text-body/BoardTextBody';
import CalendarIcon from '@/shared-components/icons/components/forms/CalendarIcon';
import FixedCharInput from '@/shared-components/inputs/components/FixedCharInput';
import ResizableTextarea from '@/shared-components/inputs/components/ResizableTextarea';
import Utils from '@/utils';

import AddArticlePreview from "./preview/ArticlePreview";
import ArticleRecommendations from "./published-article/ArticleRecommendations";
import Cover from './cover';
import Header from './inside-header';

import "./index.css";

export default function AddArticleForm({
  postData = null,
  editMode = false
}) {
  const titleFieldWrapper = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // ═════ PULL profileData from context ═════
  const { profileData } = useContext(ProfileDataContext);

  // sheet open state
  const [recsOpen, setRecsOpen] = useState(false);

  // If we arrived here via a "Save as draft" dialog, it's in location.state.postData
  const draftFromNav = location.state?.postData;
  // Otherwise use the prop postData (published-edit), or fallback to draftFromNav
  const effectivePostData = postData || draftFromNav;

  // pick the owner, whether gallery or artist
  const owner = effectivePostData?.gallery || effectivePostData?.artist || effectivePostData?.collector;

  // Use either the gallery's username on the "edit" object, or else the
  // currently‐logged‐in user_username from context:
  const userUsername =
    // If editing a published article, owner.user_username is already set:
    owner?.user_username
      ? owner.user_username
      : profileData?.user_username;

  // Build initial form state (this seeds title/desc/cover/etc.)
  const initialFormState = useMemo(() => {
    const base = Utils.initPostFormState(effectivePostData);
    return {
      ...base,
      post_status: effectivePostData?.post_status ?? base.post_status,
      publishedAt: effectivePostData?.publishedAt ?? base.publishedAt,
    };
  }, [effectivePostData]);

  const [formState, setFormState] = useState(initialFormState);
  const [membersAudienceEnabled, setMembersAudienceEnabled] = useState(null);

  useEffect(() => {
    let active = true;
    ChannelsService.getContentAudienceEligibility()
      .then(({ response, data }) => {
        if (!active) return;
        setMembersAudienceEnabled(Boolean(response?.ok && data?.membersAudienceEnabled));
      })
      .catch(() => {
        if (active) setMembersAudienceEnabled(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (membersAudienceEnabled === false && formState.audience === 'vip_only') {
      setFormState(prev => ({ ...prev, audience: 'public' }));
    }
  }, [membersAudienceEnabled, formState.audience]);
  
  // ensure our formState has recommendedArticles
  useEffect(() => {
    if (!Array.isArray(formState.recommendedArticles)) {
      setFormState(s => ({ ...s, recommendedArticles: [] }));
    }
  }, [formState.recommendedArticles]);

  // ─── Hydrate everything from post_container on mount ───
  useEffect(() => {
    const c = effectivePostData?.post_container;
    if (!c) return;

    // 1) Build a brand-new post_structure array in the right order
    // dedupe each group by its id
    const uniquePicIds     = [...new Set((c.post_pictures || []).map(pic => pic.pictureId))];
    const uniqueArtIds     = [...new Set((c.post_artworks || []).map(a   => a.artworkId))];
    
    const newStructure = [
      ...(c.post_texts    || []).map(t => ({ type: 'post_texts',    id: t.textId    })),
      ...uniquePicIds   .map(id => ({ type: 'post_pictures',  id                })),
      ...uniqueArtIds   .map(id => ({ type: 'post_artworks',  id                })),
      ...(c.post_polls    || []).map(p => ({ type: 'post_polls',     id: p.pollId     })),
      ...(c.post_quizzes  || []).map(q => ({ type: 'post_quizzes',   id: q.quizId     })),
    ];

    // 2) Merge these into your formState so BoardTextBody can render them
    setFormState(prev => ({
      ...prev,
      post_structure: newStructure,
      post_texts: c.post_texts    || [],
      post_pictures: c.post_pictures || [],
      post_artworks: c.post_artworks || [],
      post_polls: (c.post_polls   || []).map(p => ({ ...p, published: true })),
      post_quizzes: (c.post_quizzes || []).map(q => ({ ...q, published: true })),
      // bring in existing recommendations
      recommendedArticles: Array.isArray(c.recommendedArticles)
        ? c.recommendedArticles.map(a => a._id)
        : []    }));
  }, [effectivePostData]);
  // ─────────────────────────────────────────────────────────

  // Other local state
  const [error, setError]                   = useState(null);
  const [isPollModalOpen, setIsPollModalOpen]       = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen]       = useState(false);
  const [isArtworkModalOpen, setIsArtworkModalOpen] = useState(false);
  const [isDraftDialogOpen, setIsDraftDialogOpen]   = useState(false);
  const [isCoverCropOpen, setIsCoverCropOpen]       = useState(false);
  const [openPublicationDate, setOpenPublicationDate] = useState(false);

  const editableRef = useRef(null);
  const [isPreview, setIsPreview] = useState(false);
  const [previewFormState, setPreviewFormState] = useState({});
  const hasScheduledPublication = Boolean(formState.publicationDate && formState.publicationTime);
  const isCalendarActive = hasScheduledPublication;

  // Preview handler (you already had this)
  const triggerPreview = () => {
    setIsPreview(true);
  };

  // Use the owner’s ID (gallery or artist) when fetching/updating recommendations
  const ownerId =
    (owner?._id) ||
    profileData?._id;
  const currentId = effectivePostData?._id || null;
  const selectedRecommendationsCount = (formState.recommendedArticles ?? []).length;

  return (
    <>
      <div className="add-post__container">
        {/* Header: back, save-draft, preview, post */}
        <Header
          formState={formState}
          setFormState={setFormState}
          formError={error}
          setFormError={setError}
          editMode={editMode}
          previewHandler={triggerPreview}
          onDraftDialogToggle={setIsDraftDialogOpen}
          editableRef={editableRef}
          fetchData={effectivePostData}
          openPublicationDate={openPublicationDate}
          setOpenPublicationDate={setOpenPublicationDate}
          previewMode={isPreview}
          membersAudienceEnabled={membersAudienceEnabled}
        />

        <main className="add-post__main">
          <form 
            onSubmit={e => e.preventDefault()}
            className="add-post__main-form"
          >
            {/* Cover */}
            <div className="add-post__cover-section">
              <Cover
                formState={formState}
                setFormState={setFormState}
                element="post_cover"
                formError={error}
                setFormError={setError}
                onCropPopupToggle={setIsCoverCropOpen}
              />
            </div>

            {/* Title & Short Description */}
            <div className="add-post__post-main-section">
              <FixedCharInput
                element="post_title"
                label="Title"
                maxChar={47}
                error={error}
                large
                margin
                autoComplete="off"  
                value={formState.post_title}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    post_title: e.target.value,
                  }))
                }
              />

              <ResizableTextarea
                className="add-post_description"
                placeholder="Short description"
                element="post_description"
                error={error}
                value={formState.post_description}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    post_description: e.target.value,
                  }))
                }
                disableResize={true}
              />
            
              {/* ——— ALWAYS SHOW THIS BUTTON ——— */}
              <div className="add-article__recs-actions">
                <button
                  type="button"
                  className={`board-header__calendar-button add-article__calendar-button-mobile${isCalendarActive ? ' board-header__calendar-button--active' : ''}`}
                  onClick={() => setOpenPublicationDate(true)}
                  aria-label="Schedule publication date"
                  aria-pressed={isCalendarActive}
                >
                  <CalendarIcon className="board-header__calendar-icon" />
                </button>
                <div
                  className="add-article__add-recs-trigger"
                  onClick={() => {
                  // Always open the sheet, even if profileData is not yet loaded.
                    setRecsOpen(true);
                  }}
                >
                Add recommended articles
                  {selectedRecommendationsCount > 0 && (
                    <>
                      {" "}
                      <span className="add-article__add-recs-count">
                        ({selectedRecommendationsCount}/3)
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>  {/* ← now closes .add-post__post-main-section */}

            {/* ─── Article Body (text blocks, polls, quizzes, pictures, artworks) ─── */}
            <div className="add-post__article-section">
              <BoardTextBody
                formState={formState}
                setFormState={setFormState}
                postMode={false}
                previewMode={false}
                // Hide the floating “+” button whenever the recs sheet is open:
                hideAddButton={isCoverCropOpen || isDraftDialogOpen || recsOpen || isPreview}
                editableRef={editableRef}
              />
            </div> {/* ← now a sibling of the post-main section */}
          </form>

          {/**
       * ——— IMPORTANT: Render the bottom‐sheet **outside** of the <form> …
       */}
          <ArticleRecommendations
            open={recsOpen}
            close={() => setRecsOpen(false)}
            selected={formState.recommendedArticles}
            onChange={list => setFormState(s => ({ ...s, recommendedArticles: list }))}
            maxItems={3}
            galleryId={ownerId}
            currentArticleId={currentId}
          />
        </main>
      </div>
      {isPreview && (
        <AddArticlePreview
          formState={formState}
          onClose={() => setIsPreview(false)}
          editableRef={editableRef}
          setFormError={setError}
          membersAudienceEnabled={membersAudienceEnabled}
        />
      )}
    </>
  );
}
